import React, { useState, useEffect } from "react";
import API_ROUTES from "./config/apiConfig";
import { getPublicRequest } from "../api/auth";

export default function ProductDetails({ product: productProp }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("description");
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeImage, setActiveImage] = useState("/no-image.png");

  useEffect(() => {
    const getId = () => {
      let id = null;
      if (productProp) {
        id = productProp._id || productProp.id;
      }
      if (!id) {
        const parts = window.location.pathname.split("/");
        id = parts[parts.length - 1];
      }
      return id;
    };

    const id = getId();

    if (id && id !== "undefined") {
      fetchProduct(id);
    } else {
      setLoading(false);
    }
  }, [productProp]);

  const fetchProduct = async (id) => {
    try {
      setLoading(true);
      const res = await getPublicRequest(API_ROUTES.GET_PRODUCT_BY_ID(id));

      if (res?.success) {
        setProduct(res.data);
        const image =
          res.data?.images?.find((x) => x.isPrimary)?.url ||
          res.data?.images?.[0]?.url ||
          "/no-image.png";
        setActiveImage(image);
      }
    } catch (error) {
      console.log("Product error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const resize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const toggleAccordion = (tabKey) => {
    setExpandedAccordion(expandedAccordion === tabKey ? null : tabKey);
  };

  if (loading) {
    return <div className="text-center py-5">Loading Product...</div>;
  }

  if (!product) {
    return <div className="text-center py-5">Product not found</div>;
  }

  const isMobile = windowWidth <= 768;

  const scrollToLeads = () => {
    const el = document.getElementById("bulk-leads");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="pdp-wrapper overflow-hidden">
      {/* BREADCRUMB */}
      <div className="container breadcrumb-row">
        <span>Home</span>
        &nbsp;&gt;&nbsp;
        <span>{product.categoryId?.name || "Products"}</span>
        &nbsp;&gt;&nbsp;
        <span className="active">{product.title}</span>
      </div>

      {/* HERO SECTION */}
      <section className="container pdp-hero-grid">
        <div className="gallery-section-box">
          <div className="thumbnails-stack">
            {product.images?.map((img, index) => (
              <div
                key={index}
                className={`thumb-wrapper ${activeImage === img.url ? "active" : ""}`}
                onClick={() => setActiveImage(img.url)}
              >
                <img src={img.url} alt={product.title} />
              </div>
            ))}
          </div>

          <div className="featured-image-view">
            <img src={activeImage} alt={product.title} />
          </div>
        </div>

        <div className="product-summary-box">
          <h1 className="pdp-title">{product.title}</h1>
          <p className="pdp-subtitle">{product.categoryId?.name}</p>
          <p className="pdp-sku">SKU : {product.productCode || product._id}</p>
          <p className="pdp-short-desc">
            {product.shortDescription || product.description}
          </p>

          {/* BULLET HIGHLIGHTS */}
          <div className="bullet-highlights-grid">
            <div>✨ Premium Design</div>
            <div>🎨 Hand Crafted Finish</div>
            <div>💡 Energy Efficient Lighting</div>
            <div>🛡 Quality Assurance</div>
          </div>

          {/* MAKE A CUSTOM INQUIRY */}
          <div className="pricing-tier-card">
            <h4>Make a Custom Inquiry</h4>
            <p>Custom dimensions and project requirements available.</p>

            <div className="inquiry-buttons-group">
              <a href="tel:+919350824043" className="btn-inquiry btn-call">
                📞 Call Us
              </a>
              <a
                href="https://wa.me/919350824043"
                className="btn-inquiry btn-whatsapp"
                target="_blank"
                rel="noopener noreferrer"
              >
                💬 WhatsApp
              </a>
              <button
                type="button"
                className="btn-inquiry btn-email"
                onClick={scrollToLeads}
              >
                ✉️ Send Inquiry
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ATTRIBUTES BAR */}
      <section className="container attributes-pdp-bar">
        <div className="attr-item">
          <span>🧱</span>
          <div>
            <h4>Category</h4>
            <p>{product.categoryId?.name || "-"}</p>
          </div>
        </div>

        <div className="attr-item">
          <span>📦</span>
          <div>
            <h4>Sub Category</h4>
            <p>{product.subCategory?.name || "-"}</p>
          </div>
        </div>

        <div className="attr-item">
          <span>💡</span>
          <div>
            <h4>Stock</h4>
            <p>{product.stock}</p>
          </div>
        </div>

        <div className="attr-item">
          <span>🛡️</span>
          <div>
            <h4>Warranty</h4>
            <p>{product.warrantyDescription || "1 Year Coverage"}</p>
          </div>
        </div>
      </section>

      {/* SPECIFICATION TABS & ACCORDION SECTION */}
      <section className="container specs-tabbed-section">
        {!isMobile && (
          <div className="tabs-header-row desktop-only-tabs">
            <button
              className={activeTab === "description" ? "tab-link-btn active" : "tab-link-btn"}
              onClick={() => setActiveTab("description")}
            >
              DESCRIPTION
            </button>
            <button
              className={activeTab === "specifications" ? "tab-link-btn active" : "tab-link-btn"}
              onClick={() => setActiveTab("specifications")}
            >
              SPECIFICATIONS
            </button>
            <button
              className={activeTab === "dimensions" ? "tab-link-btn active" : "tab-link-btn"}
              onClick={() => setActiveTab("dimensions")}
            >
              DIMENSIONS
            </button>
            <button
              className={activeTab === "shipping" ? "tab-link-btn active" : "tab-link-btn"}
              onClick={() => setActiveTab("shipping")}
            >
              SHIPPING
            </button>
            <button
              className={activeTab === "care" ? "tab-link-btn active" : "tab-link-btn"}
              onClick={() => setActiveTab("care")}
            >
              CARE INSTRUCTIONS
            </button>
          </div>
        )}

        {!isMobile && (
          <div className="tab-body-viewport desktop-only-tabs">
            {activeTab === "description" && (
              <div className="placeholder-text-pdp">
                <h3>{product.title}</h3>
                <p>{product.description || "No description available"}</p>
              </div>
            )}

            {activeTab === "specifications" && (
              <div className="pdp-specs-only-view">
                <h3>Product Specifications</h3>
                <div className="specs-table-grid">
                  <div className="table-row">
                    <strong>Product Code</strong>
                    <span>{product.productCode || "-"}</span>
                  </div>
                  <div className="table-row">
                    <strong>Category</strong>
                    <span>{product.categoryId?.name || "-"}</span>
                  </div>
                  <div className="table-row">
                    <strong>Sub Category</strong>
                    <span>{product.subCategory?.name || "-"}</span>
                  </div>
                  <div className="table-row">
                    <strong>Status</strong>
                    <span>{product.isActive ? "Active" : "Inactive"}</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "dimensions" && (
              <div className="dimensions-sub-grid">
                <h3>Dimensions</h3>
                <p>Length: {product.dimensions?.length || "-"}</p>
                <p>Width: {product.dimensions?.width || "-"}</p>
                <p>Height: {product.dimensions?.height || "-"}</p>
                <p>Weight: {product.dimensions?.weight || "-"}</p>
              </div>
            )}

            {activeTab === "shipping" && (
              <div className="placeholder-text-pdp">
                <h3>Shipping & Returns</h3>
                <p>
                  {(
                    product.shippingReturns || "Safe and secure delivery available."
                  ).replace(/<[^>]*>/g, "")}
                </p>
              </div>
            )}

            {activeTab === "care" && (
              <div className="placeholder-text-pdp">
                <h3>Care Instructions</h3>
                {product.careInstructions ? (
                  <div
                    className="ck-content"
                    dangerouslySetInnerHTML={{ __html: product.careInstructions }}
                  />
                ) : (
                  <p>Handle with care. Wipe with dry cloth only.</p>
                )}
              </div>
            )}
          </div>
        )}

        {isMobile && (
          <div className="mobile-accordion-container">
            <div className="accordion-item">
              <button className="accordion-header" onClick={() => toggleAccordion("description")}>
                <span>Description</span>
                <span className="accordion-icon">{expandedAccordion === "description" ? "−" : "+"}</span>
              </button>
              {expandedAccordion === "description" && (
                <div className="accordion-content">
                  <p>{product.description || "No description available"}</p>
                </div>
              )}
            </div>

            <div className="accordion-item">
              <button className="accordion-header" onClick={() => toggleAccordion("specifications")}>
                <span>Specifications</span>
                <span className="accordion-icon">{expandedAccordion === "specifications" ? "−" : "+"}</span>
              </button>
              {expandedAccordion === "specifications" && (
                <div className="accordion-content">
                  <div className="specs-table-grid">
                    <div className="table-row">
                      <strong>Product Code:</strong> <span>{product.productCode || "-"}</span>
                    </div>
                    <div className="table-row">
                      <strong>Category:</strong> <span>{product.categoryId?.name || "-"}</span>
                    </div>
                    <div className="table-row">
                      <strong>Sub Category:</strong> <span>{product.subCategory?.name || "-"}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="accordion-item">
              <button className="accordion-header" onClick={() => toggleAccordion("dimensions")}>
                <span>Dimensions</span>
                <span className="accordion-icon">{expandedAccordion === "dimensions" ? "−" : "+"}</span>
              </button>
              {expandedAccordion === "dimensions" && (
                <div className="accordion-content">
                  <p>Length: {product.dimensions?.length || "-"}</p>
                  <p>Width: {product.dimensions?.width || "-"}</p>
                  <p>Height: {product.dimensions?.height || "-"}</p>
                  <p>Weight: {product.dimensions?.weight || "-"}</p>
                </div>
              )}
            </div>

            <div className="accordion-item">
              <button className="accordion-header" onClick={() => toggleAccordion("shipping")}>
                <span>Shipping & Returns</span>
                <span className="accordion-icon">{expandedAccordion === "shipping" ? "−" : "+"}</span>
              </button>
              {expandedAccordion === "shipping" && (
                <div className="accordion-content">
                  <p>
                    {(product.shippingReturns || "Safe and secure delivery available.").replace(
                      /<[^>]*>/g,
                      ""
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className="accordion-item">
              <button className="accordion-header" onClick={() => toggleAccordion("care")}>
                <span>Care Instructions</span>
                <span className="accordion-icon">{expandedAccordion === "care" ? "−" : "+"}</span>
              </button>
              {expandedAccordion === "care" && (
                <div className="accordion-content">
                  <p>Handle with care. Wipe with dry cloth only.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* COMMERCIAL LEADS FORM SECTION */}
      <section id="bulk-leads" className="container bulk-leads-section">
        <div className="leads-grid-box">
          <div className="leads-branding-side">
            <span className="leads-badge">B2B & Commercial Supply</span>
            <h3>Looking for Commercial Scaling?</h3>
            <p>
              We partner with interior designers, architects, hotels, and corporate offices 
              to deliver bespoke handcrafted products at scale.
            </p>

            <ul className="leads-benefits-list">
              <li>
                <span className="benefit-icon">🏷️</span>
                <div>
                  <strong>Attractive Wholesale Pricing</strong>
                  <p>Special bulk order tier pricing and custom quotes.</p>
                </div>
              </li>
              <li>
                <span className="benefit-icon">📐</span>
                <div>
                  <strong>Custom Dimensions & Design</strong>
                  <p>Tailor designs to match your specific project theme.</p>
                </div>
              </li>
              <li>
                <span className="benefit-icon">🚚</span>
                <div>
                  <strong>Priority Manufacturing & Shipping</strong>
                  <p>Dedicated account manager with end-to-end support.</p>
                </div>
              </li>
            </ul>
          </div>

          <form className="leads-form-fields" onSubmit={(e) => e.preventDefault()}>
            <h4>Get a Custom Project Quote</h4>
            <p className="form-subheading">Fill in your requirements and our team will get back within 24 hours.</p>

            <div className="form-row">
              <input type="text" placeholder="Full Name *" required />
              <input type="text" placeholder="Company / Firm Name" />
            </div>

            <div className="form-row">
              <input type="email" placeholder="Email Address *" required />
              <input type="tel" placeholder="Phone / WhatsApp No. *" required />
            </div>

            <textarea 
              placeholder="Project details (Quantity, custom sizes, timeline, or special requirements) *" 
              rows={4} 
              required 
            />

            <button type="submit" className="btn-leads-submit">
              REQUEST BULK QUOTE
            </button>

            <span className="privacy-note">🔒 Your information is safe with us. No spam.</span>
          </form>
        </div>
      </section>

      {/* FIXED MOBILE ACTION BAR */}
      {isMobile && (
        <div className="fixed-mobile-pdp-action-bar">
          <a href="tel:+919350824043" className="mobile-action-btn-pdp call-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#e91e63">
              <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
            <span>Call</span>
          </a>

          <a
            href="https://wa.me/919350824043"
            className="mobile-action-btn-pdp whatsapp-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#a855f7">
              <path d="M20.52 3.48A11.93 11.93 0 0012 0C5.37 0 0 5.37 0 12c0 2.12.55 4.19 1.6 6.01L0 24l6.17-1.62A11.9 11.9 0 0012 24c6.63 0 12-5.37 12-12 0-3.21-1.25-6.23-3.48-8.52zM12 22c-1.81 0-3.58-.48-5.14-1.4l-.37-.22-3.82 1 1.02-3.72-.24-.38A9.93 9.93 0 012 12c0-5.51 4.49-10 10-10 2.67 0 5.18 1.04 7.07 2.93A9.93 9.93 0 0122 12c0 5.51-4.49 10-10 10z"/>
            </svg>
            <span>WhatsApp</span>
          </a>

          <button className="mobile-action-btn-pdp enquire-btn" onClick={scrollToLeads}>
            Enquire
          </button>
        </div>
      )}
    </div>
  );
}